import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Users, Building2, MessageSquare, TrendingUp, 
  LogOut, Home as HomeIcon, Eye, Edit, Trash2, Plus,
  CheckCircle, XCircle, Clock, AlertCircle, X, UserPlus,
  DollarSign, CreditCard, BarChart3, Calendar, Filter,
  Mail, LinkIcon, Send, Download, KeyRound, PlayCircle, GraduationCap,
  Bell, Check, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { authService } from "../services/api";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize a phone number for wa.me link: strip non-digits and add 52 prefix for MX numbers.
// - 10 digits → prepend 52
// - 11 digits starting with "1" (US-style typo on MX number) → strip the 1 and prepend 52
// - Otherwise return digits as-is
const normalizeWaNumber = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  if (digits.length === 10) return `52${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `52${digits.slice(1)}`;
  return digits;
};

export const SuperAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [paymentStats, setPaymentStats] = useState(null);
  const [allPayments, setAllPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsQuery, setPaymentsQuery] = useState("");
  const [paymentsFilter, setPaymentsFilter] = useState("all");
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProperties, setUserProperties] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [allProperties, setAllProperties] = useState([]);
  
  // Date filter states - Default to last 7 days
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      from: sevenDaysAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  const defaultDates = getDefaultDates();
  const [dateFrom, setDateFrom] = useState(defaultDates.from);
  const [dateTo, setDateTo] = useState(defaultDates.to);
  const [filteredStats, setFilteredStats] = useState(null);
  
  // Modal states
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    password: "",
    company_name: "",
    phone: ""
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Email logs & Webhooks
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogTotal, setEmailLogTotal] = useState(0);
  const [emailTypeFilter, setEmailTypeFilter] = useState("all");
  const [webhooks, setWebhooks] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ name: "", url: "", secret: "", events: [] });

  // Learning videos
  const [learningVideos, setLearningVideos] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: "", description: "", video_url: "", category: "general", visible_plans: ["basico", "pro", "premium", "agencia"] });

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Email preview
  const [previewEmail, setPreviewEmail] = useState(null);

  useEffect(() => {
    const user = authService.getUser();
    if (!user || user.role !== "admin") {
      toast.error("Acceso denegado");
      navigate("/login");
      return;
    }
    loadData();
  }, [navigate]);

  // Filter data when dates change
  useEffect(() => {
    if (stats && users) {
      applyDateFilter();
    }
  }, [dateFrom, dateTo, stats, users, allProperties, paymentStats]);

  const applyDateFilter = () => {
    if (!dateFrom && !dateTo) {
      setFilteredStats(null);
      return;
    }

    const fromDate = dateFrom ? new Date(dateFrom) : new Date("1970-01-01");
    const toDate = dateTo ? new Date(dateTo + "T23:59:59") : new Date("2099-12-31");

    // Filter users by created_at
    const filteredUsers = users.filter(u => {
      if (!u.created_at) return false;
      const userDate = new Date(u.created_at);
      return userDate >= fromDate && userDate <= toDate;
    });

    // Filter properties by created_at
    const filteredProperties = allProperties.filter(p => {
      if (!p.created_at) return false;
      const propDate = new Date(p.created_at);
      return propDate >= fromDate && propDate <= toDate;
    });

    // Calculate filtered stats
    const newUsersInPeriod = filteredUsers.length;
    const newPropertiesInPeriod = filteredProperties.length;
    
    // Filter payments if available
    let filteredPaymentStats = null;
    if (paymentStats?.recent_payments) {
      const filteredPayments = paymentStats.recent_payments.filter(p => {
        if (!p.created_at) return false;
        const payDate = new Date(p.created_at);
        return payDate >= fromDate && payDate <= toDate;
      });
      
      const totalRevenue = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      filteredPaymentStats = {
        total_revenue: totalRevenue,
        payments_count: filteredPayments.length,
        recent_payments: filteredPayments
      };
    }

    // Count by plan in period
    const planCounts = {};
    filteredUsers.forEach(u => {
      const plan = u.plan || 'basico';
      planCounts[plan] = (planCounts[plan] || 0) + 1;
    });

    setFilteredStats({
      users: newUsersInPeriod,
      properties: newPropertiesInPeriod,
      byPlan: planCounts,
      payments: filteredPaymentStats,
      dateRange: { from: dateFrom, to: dateTo }
    });
  };

  const clearDateFilter = () => {
    setDateFrom("");
    setDateTo("");
    setFilteredStats(null);
  };

  const loadData = async () => {
    try {
      const [statsRes, usersRes, propsRes, paymentStatsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/properties`),
        axios.get(`${API}/admin/payment-stats`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/notifications?limit=30`).catch(() => ({ data: { notifications: [], unread_count: 0 } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAllProperties(propsRes.data);
      setPaymentStats(paymentStatsRes.data);
      if (notifsRes.data) {
        setNotifications(notifsRes.data.notifications || []);
        setUnreadCount(notifsRes.data.unread_count || 0);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const loadAllPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payments/all`, {
        params: {
          page: paymentsPage,
          page_size: 25,
          status_filter: paymentsFilter,
          ...(paymentsQuery ? { q: paymentsQuery } : {})
        },
        timeout: 90000
      });
      setAllPayments(res.data.payments || []);
      setPaymentsTotal(res.data.total || 0);
      setPaymentsHasMore(res.data.has_more || false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error(`Error al cargar pagos: ${msg}`);
      console.error("[loadAllPayments] error:", e);
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Reload payments when filter, page or query changes (debounced for query)
  useEffect(() => {
    if (activeTab !== "overview") return;
    const t = setTimeout(() => { loadAllPayments(); }, paymentsQuery ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsPage, paymentsFilter, paymentsQuery, activeTab]);

  const loadNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=30`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) { /* ignore */ }
  };

  const markNotificationRead = async (id) => {
    await axios.put(`${API}/admin/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${API}/admin/notifications/read-all`);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadUserProperties = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/properties`);
      setUserProperties(res.data);
    } catch (error) {
      toast.error("Error al cargar propiedades");
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    await loadUserProperties(user.id);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success("Usuario actualizado");
      loadData();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const handleSyncStripe = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/sync-stripe`);
      if (res.data.success) {
        toast.success("Sincronizado con Stripe exitosamente");
        // Reload user data
        const userRes = await axios.get(`${API}/admin/users/${userId}`);
        setSelectedUser(userRes.data);
        loadData();
      } else {
        toast.error(res.data.message || "No se encontró en Stripe");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al sincronizar con Stripe");
    }
  };

  const handleGeneratePaymentLink = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/generate-payment-link`);
      if (res.data.success) {
        // Copy link to clipboard
        navigator.clipboard.writeText(res.data.payment_url);
        toast.success(
          `Link de pago generado para ${res.data.user_email} - Plan ${res.data.plan} ($${res.data.amount} MXN). ¡Copiado al portapapeles!`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar link de pago");
    }
  };

  const handleSendExpiredReminders = async (userId = null) => {
    const confirmMsg = userId 
      ? "¿Enviar correo de recordatorio a este usuario?"
      : "¿Enviar correo de recordatorio a TODOS los usuarios expirados?";
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await axios.post(`${API}/admin/send-expired-reminder`, { user_id: userId });
      if (res.data.success) {
        toast.success(`Correos enviados: ${res.data.sent_count} de ${res.data.total_users}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar correos");
    }
  };

  const handleSendPendingReminders = async (userId = null) => {
    const confirmMsg = userId
      ? "¿Enviar correo de recordatorio a este usuario?"
      : "¿Enviar recordatorio a TODOS los usuarios con pago pendiente?";
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await axios.post(`${API}/admin/send-pending-reminder`, { user_id: userId });
      if (res.data.success) {
        toast.success(`Correos enviados: ${res.data.sent_count} de ${res.data.total_users}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar correos");
    }
  };

  const loadEmailLogs = async (type = "all") => {
    try {
      const params = type !== "all" ? `?email_type=${type}` : "";
      const res = await axios.get(`${API}/admin/email-logs${params}`);
      setEmailLogs(res.data.logs || []);
      setEmailLogTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error loading email logs:", error);
    }
  };

  const viewEmailContent = async (emailId) => {
    try {
      const res = await axios.get(`${API}/admin/email-logs/${emailId}`);
      setPreviewEmail(res.data);
    } catch (error) {
      toast.error("No se pudo cargar el contenido del email");
    }
  };

  const loadWebhooks = async () => {
    try {
      const [whRes, logRes] = await Promise.all([
        axios.get(`${API}/admin/webhooks`),
        axios.get(`${API}/admin/webhook-logs`)
      ]);
      setWebhooks(whRes.data || []);
      setWebhookLogs(logRes.data || []);
    } catch (error) {
      console.error("Error loading webhooks:", error);
    }
  };

  const handleCreateWebhook = async () => {
    if (!webhookForm.url || webhookForm.events.length === 0) {
      toast.error("URL y al menos un evento son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/admin/webhooks`, webhookForm);
      toast.success("Webhook creado");
      setShowWebhookModal(false);
      setWebhookForm({ name: "", url: "", secret: "", events: [] });
      loadWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear webhook");
    }
  };

  const handleToggleWebhook = async (id, isActive) => {
    try {
      await axios.put(`${API}/admin/webhooks/${id}`, { is_active: !isActive });
      loadWebhooks();
    } catch (error) {
      toast.error("Error al actualizar webhook");
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm("¿Eliminar este webhook?")) return;
    try {
      await axios.delete(`${API}/admin/webhooks/${id}`);
      toast.success("Webhook eliminado");
      loadWebhooks();
    } catch (error) {
      toast.error("Error al eliminar webhook");
    }
  };

  const handleTestWebhook = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/webhooks/${id}/test`);
      if (res.data.success) {
        toast.success(`Test enviado (status: ${res.data.status_code})`);
      } else {
        toast.error(`Error: ${res.data.error}`);
      }
    } catch (error) {
      toast.error("Error al probar webhook");
    }
  };

  const toggleWebhookEvent = (event) => {
    setWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  // Learning Videos
  const loadLearningVideos = async () => {
    try {
      const res = await axios.get(`${API}/admin/learning-videos`);
      setLearningVideos(res.data || []);
    } catch (error) {
      console.error("Error loading learning videos:", error);
    }
  };

  const handleSaveVideo = async () => {
    if (!videoForm.title || !videoForm.video_url) {
      toast.error("Titulo y URL del video son requeridos");
      return;
    }
    try {
      if (editingVideo) {
        await axios.put(`${API}/admin/learning-videos/${editingVideo.id}`, videoForm);
        toast.success("Video actualizado");
      } else {
        await axios.post(`${API}/admin/learning-videos`, videoForm);
        toast.success("Video creado");
      }
      setShowVideoModal(false);
      setEditingVideo(null);
      setVideoForm({ title: "", description: "", video_url: "", category: "general", visible_plans: ["basico", "pro", "premium", "agencia"] });
      loadLearningVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar video");
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title, description: video.description || "", video_url: video.video_url,
      category: video.category || "general",
      visible_plans: video.visible_plans || ["basico", "pro", "premium", "agencia"]
    });
    setShowVideoModal(true);
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm("¿Eliminar este video?")) return;
    try {
      await axios.delete(`${API}/admin/learning-videos/${id}`);
      toast.success("Video eliminado");
      loadLearningVideos();
    } catch (error) {
      toast.error("Error al eliminar video");
    }
  };

  const handleToggleVideoActive = async (video) => {
    try {
      await axios.put(`${API}/admin/learning-videos/${video.id}`, { is_active: !video.is_active });
      loadLearningVideos();
    } catch (error) {
      toast.error("Error al actualizar video");
    }
  };

  const toggleVideoPlan = (plan) => {
    setVideoForm(prev => ({
      ...prev,
      visible_plans: prev.visible_plans.includes(plan)
        ? prev.visible_plans.filter(p => p !== plan)
        : [...prev.visible_plans, plan]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error("Nombre, email y contraseña son requeridos");
      return;
    }
    
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUserForm);
      toast.success("Usuario creado exitosamente");
      setShowCreateUserModal(false);
      setNewUserForm({ name: "", email: "", password: "", company_name: "", phone: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Usuario eliminado");
      setShowDeleteConfirm(null);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("¿Eliminar esta propiedad?")) return;
    
    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`);
      toast.success("Propiedad eliminada");
      loadData();
      if (selectedUser) {
        loadUserProperties(selectedUser.id);
      }
    } catch (error) {
      toast.error("Error al eliminar propiedad");
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-700",
      trialing: "bg-blue-100 text-blue-700",
      expired: "bg-red-100 text-red-700",
      canceled: "bg-gray-100 text-gray-700",
      past_due: "bg-orange-100 text-orange-700",
      pending_payment: "bg-amber-100 text-amber-700"
    };
    const labels = {
      active: "Activo",
      trialing: "Prueba",
      expired: "Expirado",
      canceled: "Cancelado",
      past_due: "Pago Atrasado",
      pending_payment: "Pago Pendiente"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.canceled}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const styles = {
      trial: "bg-blue-100 text-blue-700",
      basico: "bg-gray-100 text-gray-700",
      pro: "bg-purple-100 text-purple-700",
      premium: "bg-yellow-100 text-yellow-700",
      agencia: "bg-pink-100 text-pink-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[plan] || styles.basico}`}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
      </span>
    );
  };

  const handleExportCSV = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type}_inmopages.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Archivo exportado");
    } catch (error) {
      toast.error("Error al exportar");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/lv2qzyun_inmopages-fondooscuro.png" 
                alt="InmoPages" 
                className="h-8"
              />
              <span className="hidden sm:inline ml-2 px-2 py-0.5 bg-brand-purple text-white text-xs rounded">SUPER ADMIN</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Notifications Bell */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:text-white hover:bg-gray-800 relative"
                  onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications) loadNotifications(); }}
                  data-testid="notifications-bell"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Notifications Dropdown - centered on mobile */}
                {showNotifications && (
                  <div className="fixed sm:absolute inset-x-4 sm:inset-x-auto sm:right-0 top-16 sm:top-full sm:mt-2 w-auto sm:w-96 bg-white rounded-lg shadow-2xl border z-50 max-h-[480px] overflow-hidden" data-testid="notifications-dropdown">
                    <div className="p-3 border-b flex items-center justify-between bg-gray-50">
                      <h3 className="font-semibold text-gray-900 text-sm">Notificaciones</h3>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-brand-purple hover:underline">
                            Marcar todas leídas
                          </button>
                        )}
                        <button onClick={() => setShowNotifications(false)}>
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[400px]">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-gray-400 text-sm">
                          Sin notificaciones
                        </div>
                      ) : notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${!notif.is_read ? 'bg-purple-50/50' : ''}`}
                          onClick={() => { if (!notif.is_read) markNotificationRead(notif.id); }}
                          data-testid={`notification-${notif.id}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              notif.type === 'new_registration' ? 'bg-purple-500' :
                              notif.type === 'new_subscription' ? 'bg-green-500' :
                              notif.type === 'subscription_canceled' ? 'bg-orange-500' :
                              notif.type === 'payment_failed' || notif.type === 'subscription_expired' ? 'bg-red-500' :
                              'bg-blue-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {notif.created_at ? new Date(notif.created_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                            {!notif.is_read && (
                              <div className="w-2 h-2 bg-brand-purple rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm" className="text-white hover:text-white hover:bg-gray-800">
                  Mi Panel
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:text-white hover:bg-gray-800">
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
        {/* Date Filter */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <span className="font-medium text-gray-700">Filtrar por fecha:</span>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="dateFrom" className="text-sm text-gray-500">Desde</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="dateTo" className="text-sm text-gray-500">Hasta</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button variant="outline" size="sm" onClick={clearDateFilter}>
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>
            {filteredStats && (
              <div className="mt-3 text-sm text-brand-purple font-medium">
                📊 Mostrando datos del {filteredStats.dateRange.from || 'inicio'} al {filteredStats.dateRange.to || 'hoy'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{filteredStats ? 'Nuevos Usuarios' : 'Total Usuarios'}</p>
                  <p className="text-3xl font-bold">{filteredStats ? filteredStats.users : (stats?.total_users || 0)}</p>
                </div>
                <Users className="w-10 h-10 text-brand-purple opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Usuarios Activos</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.active_users || 0}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Expirados</p>
                  <p className="text-3xl font-bold text-red-600">{stats?.expired_users || 0}</p>
                </div>
                <XCircle className="w-10 h-10 text-red-500 opacity-50" />
              </div>
              {stats?.expired_users > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendExpiredReminders()}
                  className="w-full mt-3 text-red-600 border-red-300 hover:bg-red-100"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar recordatorio
                </Button>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{filteredStats ? 'Nuevas Propiedades' : 'Propiedades'}</p>
                  <p className="text-3xl font-bold">{filteredStats ? filteredStats.properties : (stats?.total_properties || 0)}</p>
                </div>
                <Building2 className="w-10 h-10 text-brand-purple opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-3xl font-bold">{stats?.total_leads || 0}</p>
                </div>
                <MessageSquare className="w-10 h-10 text-brand-purple opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">{filteredStats ? 'Ingresos en Periodo' : 'Ingresos Totales'}</p>
                  <p className="text-2xl font-bold text-green-700">
                    ${((filteredStats?.payments?.total_revenue || paymentStats?.total_revenue || 0) / 100).toLocaleString()} MXN
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan Distribution with Active Subscriptions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{filteredStats ? 'Nuevos Usuarios por Plan' : 'Usuarios por Plan'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(filteredStats?.byPlan || stats?.users_by_plan || {}).map(([plan, count]) => (
                  <div key={plan} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-gray-500 capitalize">{plan}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Usuarios por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-2xl font-bold text-green-700">{stats?.users_by_status?.active || 0}</p>
                  <p className="text-sm text-green-600">Activos</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl font-bold text-blue-700">{stats?.users_by_status?.trialing || 0}</p>
                  <p className="text-sm text-blue-600">En Prueba</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-2xl font-bold text-red-700">{stats?.users_by_status?.expired || 0}</p>
                  <p className="text-sm text-red-600">Expirados</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-2xl font-bold text-amber-700">{stats?.users_by_status?.pending_payment || 0}</p>
                  <p className="text-sm text-amber-600">Pago Pendiente</p>
                  {(stats?.users_by_status?.pending_payment || 0) > 0 && (
                    <button
                      onClick={() => handleSendPendingReminders()}
                      className="mt-2 text-xs text-amber-700 hover:text-amber-900 underline"
                      data-testid="send-pending-reminder-btn"
                    >
                      Enviar recordatorio
                    </button>
                  )}
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-2xl font-bold text-orange-700">{stats?.users_by_status?.past_due || 0}</p>
                  <p className="text-sm text-orange-600">Pago Atrasado</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-2xl font-bold text-gray-700">{stats?.users_by_status?.canceled || 0}</p>
                  <p className="text-sm text-gray-600">Cancelados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
          <div className="flex gap-2 w-fit">
            <Button
              size="sm"
              variant={activeTab === "overview" ? "default" : "outline"}
              onClick={() => setActiveTab("overview")}
              className={`whitespace-nowrap ${activeTab === "overview" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <BarChart3 className="w-4 h-4 mr-1.5" />
              Pagos
            </Button>
            <Button
              size="sm"
              variant={activeTab === "users" ? "default" : "outline"}
              onClick={() => setActiveTab("users")}
              className={`whitespace-nowrap ${activeTab === "users" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <Users className="w-4 h-4 mr-1.5" />
              Usuarios
            </Button>
            <Button
              size="sm"
              variant={activeTab === "properties" ? "default" : "outline"}
              onClick={() => setActiveTab("properties")}
              className={`whitespace-nowrap ${activeTab === "properties" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <Building2 className="w-4 h-4 mr-1.5" />
              Propiedades
            </Button>
            <Button
              size="sm"
              variant={activeTab === "emails" ? "default" : "outline"}
              onClick={() => { setActiveTab("emails"); loadEmailLogs(emailTypeFilter); }}
              className={`whitespace-nowrap ${activeTab === "emails" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Emails
            </Button>
            <Button
              size="sm"
              variant={activeTab === "webhooks" ? "default" : "outline"}
              onClick={() => { setActiveTab("webhooks"); loadWebhooks(); }}
              className={`whitespace-nowrap ${activeTab === "webhooks" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <LinkIcon className="w-4 h-4 mr-1.5" />
              Webhooks
            </Button>
            <Button
              size="sm"
              variant={activeTab === "learning" ? "default" : "outline"}
              onClick={() => { setActiveTab("learning"); loadLearningVideos(); }}
              className={`whitespace-nowrap ${activeTab === "learning" ? "bg-brand-purple hover:bg-brand-pink" : ""}`}
            >
              <GraduationCap className="w-4 h-4 mr-1.5" />
              Aprendizaje
            </Button>
          </div>
        </div>

        {/* Payments Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <Button 
                onClick={() => handleExportCSV("payments")}
                variant="outline"
                size="sm"
                data-testid="export-payments-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Pagos CSV
              </Button>
            </div>
            {/* Revenue by Plan */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ingresos por Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {paymentStats?.revenue_by_plan && Object.keys(paymentStats.revenue_by_plan).length > 0 ? (
                    Object.entries(paymentStats.revenue_by_plan).map(([plan, planData]) => (
                      <div key={plan} className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 capitalize mb-1">{plan}</p>
                        <p className="text-xl font-bold text-brand-purple">
                          ${((planData.total || 0) / 100).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                        </p>
                        <p className="text-xs text-gray-400">{planData.count || 0} pago(s)</p>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-4 text-center text-gray-400 py-4">
                      No hay pagos registrados aún
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* All Payments (paginated from Stripe live) */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="text-lg">Pagos (todos)</CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      type="text"
                      placeholder="Buscar nombre, email o charge id…"
                      value={paymentsQuery}
                      onChange={(e) => { setPaymentsQuery(e.target.value); setPaymentsPage(1); }}
                      className="border rounded-lg px-3 py-1.5 text-sm w-64"
                      data-testid="payments-search-input"
                    />
                    <select
                      value={paymentsFilter}
                      onChange={(e) => { setPaymentsFilter(e.target.value); setPaymentsPage(1); }}
                      className="border rounded-lg px-2 py-1.5 text-sm"
                      data-testid="payments-filter"
                    >
                      <option value="all">Todos</option>
                      <option value="paid">Pagados</option>
                      <option value="refunded">Reembolsados</option>
                      <option value="failed">Fallidos</option>
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => loadAllPayments()}
                      disabled={paymentsLoading}
                      data-testid="payments-refresh-btn"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${paymentsLoading ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/admin/payments/sync-stripe`);
                          toast.success(`${r.data.updated_count} pago(s) actualizado(s) desde Stripe`);
                          loadAllPayments();
                        } catch (e) {
                          toast.error("Error al sincronizar");
                        }
                      }}
                      className="text-purple-700 border-purple-300 hover:bg-purple-50"
                      data-testid="payments-sync-stripe-btn"
                    >
                      Sincronizar con Stripe
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {paymentsLoading ? (
                  <div className="text-center text-gray-500 py-8">Cargando pagos...</div>
                ) : !allPayments || allPayments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No hay pagos para el filtro seleccionado</p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-left text-sm">
                            <th className="pb-3 font-medium text-gray-500">Usuario</th>
                            <th className="pb-3 font-medium text-gray-500">Plan</th>
                            <th className="pb-3 font-medium text-gray-500">Monto</th>
                            <th className="pb-3 font-medium text-gray-500">Estado</th>
                            <th className="pb-3 font-medium text-gray-500">Fecha</th>
                            <th className="pb-3 font-medium text-gray-500">Recibo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allPayments.map((p) => (
                            <tr key={p.id} className="border-b hover:bg-gray-50">
                              <td className="py-3">
                                <p className="font-medium text-sm">{p.user_email}</p>
                                {p.user_name && <p className="text-xs text-gray-500">{p.user_name}</p>}
                                {p.platform_email && p.platform_email.toLowerCase() !== (p.user_email || "").toLowerCase() && (
                                  <p className="text-xs text-purple-600 mt-0.5" title="Email en la plataforma InmoPages">
                                    ↳ {p.platform_email}
                                  </p>
                                )}
                              </td>
                              <td className="py-3">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">{p.plan_name}</span>
                              </td>
                              <td className="py-3 font-semibold text-sm">
                                ${p.amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {p.currency}
                                {p.status === 'refunded' && p.amount_refunded > 0 && (
                                  <div className="text-xs text-red-600">-${p.amount_refunded.toLocaleString('es-MX')} reembolsado</div>
                                )}
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  p.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  p.status === 'refunded' ? 'bg-red-100 text-red-700' :
                                  p.status === 'failed' ? 'bg-gray-200 text-gray-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {p.status === 'paid' ? 'Pagado' : p.status === 'refunded' ? 'Reembolsado' : p.status === 'failed' ? 'Fallido' : p.status}
                                </span>
                              </td>
                              <td className="py-3 text-xs text-gray-500">{formatDate(p.created_at)}</td>
                              <td className="py-3">
                                {p.receipt_url && (
                                  <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="text-brand-purple hover:underline text-xs">
                                    Ver
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <p className="text-sm text-gray-500">
                        Página {paymentsPage} · Mostrando {allPayments.length} de {paymentsTotal} pagos
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setPaymentsPage(p => Math.max(1, p - 1)); }} disabled={paymentsPage <= 1}>
                          Anterior
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setPaymentsPage(p => p + 1); }} disabled={!paymentsHasMore}>
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg mb-3">Todos los Usuarios</CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                <Input
                  type="text"
                  placeholder="Buscar nombre, email, email Stripe, WhatsApp…"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-72"
                  data-testid="user-search-input"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                  data-testid="user-status-filter"
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="trialing">En Prueba</option>
                  <option value="expired">Expirados</option>
                  <option value="pending_payment">Pago Pendiente</option>
                  <option value="past_due">Pago Atrasado</option>
                  <option value="canceled">Cancelados</option>
                </select>
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                  data-testid="user-plan-filter"
                >
                  <option value="all">Todos los planes</option>
                  <option value="basico">Básico</option>
                  <option value="pro">Pro</option>
                  <option value="premium">Premium</option>
                  <option value="agencia">Agencia</option>
                </select>
                <Button 
                  onClick={() => handleExportCSV("users")}
                  variant="outline"
                  size="sm"
                  data-testid="export-users-btn"
                  className="whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                <Button 
                  onClick={async () => {
                    if (!window.confirm("¿Normalizar todos los números de WhatsApp? Los números MX de 10 dígitos recibirán prefijo 52 automáticamente. Esto afecta usuarios y propiedades.")) return;
                    try {
                      const r = await axios.post(`${API}/admin/migrate/normalize-whatsapp`);
                      const u = r.data.users_fixed || 0;
                      const p = r.data.properties_fixed || 0;
                      toast.success(`${u} usuario(s) y ${p} propiedad(es) normalizados`);
                      loadData();
                    } catch (e) {
                      const msg = e?.response?.data?.detail || e?.message || "Error";
                      toast.error(`Error al normalizar: ${msg}`);
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap text-green-700 border-green-300 hover:bg-green-50"
                  data-testid="normalize-whatsapp-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Normalizar WhatsApp
                </Button>
                <Button 
                  onClick={() => setShowCreateUserModal(true)}
                  className="bg-brand-purple hover:bg-brand-pink whitespace-nowrap"
                  size="sm"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuevo Usuario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-500">Usuario</th>
                      <th className="pb-3 font-medium text-gray-500">WhatsApp</th>
                      <th className="pb-3 font-medium text-gray-500">Plan</th>
                      <th className="pb-3 font-medium text-gray-500">Estado</th>
                      <th className="pb-3 font-medium text-gray-500">Próximo Cobro</th>
                      <th className="pb-3 font-medium text-gray-500">Props</th>
                      <th className="pb-3 font-medium text-gray-500">Leads</th>
                      <th className="pb-3 font-medium text-gray-500">Origen</th>
                      <th className="pb-3 font-medium text-gray-500">Registro</th>
                      <th className="pb-3 font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => statusFilter === "all" || u.subscription_status === statusFilter)
                      .filter(u => planFilter === "all" || u.plan === planFilter)
                      .filter(u => {
                        if (!userSearchQuery) return true;
                        const q = userSearchQuery.trim().toLowerCase();
                        if (!q) return true;
                        const qDigits = q.replace(/\D+/g, "");
                        return (
                          (u.name || "").toLowerCase().includes(q) ||
                          (u.email || "").toLowerCase().includes(q) ||
                          (u.stripe_email || "").toLowerCase().includes(q) ||
                          (u.company_name || "").toLowerCase().includes(q) ||
                          (qDigits.length >= 3 && (u.phone || "").includes(qDigits))
                        );
                      })
                      .map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                            {user.stripe_email && user.stripe_email.toLowerCase() !== (user.email || "").toLowerCase() && (
                              <p className="text-xs text-purple-600" title="Email vinculado en Stripe">
                                ↳ Stripe: {user.stripe_email}
                              </p>
                            )}
                            {user.role === "admin" && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4">
                          {user.phone ? (
                            <a href={`https://wa.me/${normalizeWaNumber(user.phone)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline" data-testid={`user-whatsapp-${user.id}`}>
                              {user.phone}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-4">{getPlanBadge(user.plan)}</td>
                        <td className="py-4">{getStatusBadge(user.subscription_status)}</td>
                        <td className="py-4">
                          {user.next_billing_date ? (
                            <div className="text-sm">
                              <p className="font-medium text-green-600">{formatDate(user.next_billing_date)}</p>
                              {user.stripe_status === 'trialing' && (
                                <span className="text-xs text-blue-600">Prueba</span>
                              )}
                            </div>
                          ) : user.subscription_ends_at && user.subscription_status === 'active' ? (
                            <div className="text-sm">
                              <p className="font-medium text-green-600">{formatDate(user.subscription_ends_at)}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-4">{user.property_count || 0}</td>
                        <td className="py-4">{user.lead_count || 0}</td>
                        <td className="py-4">
                          {user.utm_source || user.utm_campaign ? (
                            <div className="text-xs">
                              {user.utm_source && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{user.utm_source}</span>}
                              {user.utm_campaign && <p className="text-gray-400 mt-0.5 truncate max-w-[100px]">{user.utm_campaign}</p>}
                            </div>
                          ) : user.google_id ? (
                            <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Google</span>
                          ) : (
                            <span className="text-xs text-gray-400">Directo</span>
                          )}
                        </td>
                        <td className="py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSelectUser(user)}
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <select
                              className="text-sm border rounded px-2 py-1"
                              value={user.plan}
                              onChange={(e) => handleUpdateUser(user.id, { plan: e.target.value })}
                            >
                              <option value="basico">Básico</option>
                              <option value="pro">Pro</option>
                              <option value="premium">Premium</option>
                              <option value="agencia">Agencia</option>
                            </select>
                            {user.role !== "admin" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => setShowDeleteConfirm(user)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Properties Tab */}
        {activeTab === "properties" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg mb-3">Todas las Propiedades ({allProperties.length})</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => handleExportCSV("properties")}
                  variant="outline"
                  size="sm"
                  data-testid="export-properties-btn"
                  className="whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                <Button 
                  onClick={() => handleExportCSV("leads")}
                  variant="outline"
                  size="sm"
                  data-testid="export-leads-admin-btn"
                  className="whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Leads
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-500">Propiedad</th>
                      <th className="pb-3 font-medium text-gray-500">Propietario</th>
                      <th className="pb-3 font-medium text-gray-500">Precio</th>
                      <th className="pb-3 font-medium text-gray-500">Estado</th>
                      <th className="pb-3 font-medium text-gray-500">Creado</th>
                      <th className="pb-3 font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allProperties.map((prop) => (
                      <tr key={prop.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">
                          <div>
                            <p className="font-medium">{prop.title}</p>
                            <p className="text-sm text-gray-500">{prop.location}</p>
                          </div>
                        </td>
                        <td className="py-4">
                          <div>
                            <p className="font-medium">{prop.owner?.name || "N/A"}</p>
                            <p className="text-sm text-gray-500">{prop.owner?.email || ""}</p>
                          </div>
                        </td>
                        <td className="py-4 font-semibold text-brand-purple">{formatPrice(prop.price)}</td>
                        <td className="py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            prop.status === "active" ? "bg-green-100 text-green-700" :
                            prop.status === "sold" ? "bg-red-100 text-red-700" :
                            prop.status === "rented" ? "bg-orange-100 text-orange-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>
                            {prop.status === "active" ? "Activa" :
                             prop.status === "sold" ? "Vendida" :
                             prop.status === "rented" ? "Rentada" : prop.status}
                          </span>
                        </td>
                        <td className="py-4 text-sm text-gray-500">{formatDate(prop.created_at)}</td>
                        <td className="py-4">
                          <div className="flex gap-2">
                            <Link to={`/propiedades/${prop.slug}`} target="_blank">
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Link to={`/admin/properties/edit/${prop.id}`} state={{ from: 'super-admin' }}>
                              <Button variant="outline" size="sm">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteProperty(prop.id)}
                              data-testid={`delete-prop-${prop.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Logs Tab */}
        {activeTab === "emails" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg mb-3">Historial de Emails ({emailLogTotal})</CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={emailTypeFilter}
                  onChange={(e) => { setEmailTypeFilter(e.target.value); loadEmailLogs(e.target.value); }}
                  className="border rounded-md px-3 py-2 text-sm"
                  data-testid="email-type-filter"
                >
                  <option value="all">Todos</option>
                  <option value="welcome">Bienvenida</option>
                  <option value="subscription_expired">Suscripcion Expirada</option>
                  <option value="pending_payment_reminder">Pago Pendiente</option>
                  <option value="trial_reminder">Recordatorio Prueba</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadEmailLogs(emailTypeFilter)}
                  data-testid="refresh-email-logs-btn"
                >
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {emailLogs.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No hay emails registrados aun. Los emails enviados a partir de ahora se mostraran aqui.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="email-logs-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium text-gray-500">Fecha</th>
                        <th className="pb-3 font-medium text-gray-500">Tipo</th>
                        <th className="pb-3 font-medium text-gray-500">Destinatario</th>
                        <th className="pb-3 font-medium text-gray-500">Asunto</th>
                        <th className="pb-3 font-medium text-gray-500">Estado</th>
                        <th className="pb-3 font-medium text-gray-500">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 text-sm text-gray-500">{formatDate(log.created_at)}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.type === "welcome" ? "bg-blue-100 text-blue-700" :
                              log.type === "subscription_expired" ? "bg-red-100 text-red-700" :
                              log.type === "pending_payment_reminder" ? "bg-amber-100 text-amber-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {log.type === "welcome" ? "Bienvenida" :
                               log.type === "subscription_expired" ? "Expiracion" :
                               log.type === "pending_payment_reminder" ? "Pago Pendiente" :
                               log.type === "trial_reminder" ? "Recordatorio" : log.type}
                            </span>
                          </td>
                          <td className="py-3">
                            <p className="font-medium text-sm">{log.recipient_name}</p>
                            <p className="text-xs text-gray-500">{log.recipient_email}</p>
                          </td>
                          <td className="py-3 text-sm text-gray-600 max-w-[200px] truncate">
                            {log.subject || "-"}
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}>
                              {log.status === "sent" ? "Enviado" : "Error"}
                            </span>
                          </td>
                          <td className="py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewEmailContent(log.id)}
                              data-testid={`view-email-${log.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Preview Modal */}
        {previewEmail && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewEmail(null)}>
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="email-preview-modal">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-900">{previewEmail.subject || "Email"}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Para: {previewEmail.recipient_name} ({previewEmail.recipient_email}) — {previewEmail.created_at ? new Date(previewEmail.created_at).toLocaleString('es-MX') : ''}
                  </p>
                </div>
                <button onClick={() => setPreviewEmail(null)} className="p-1 hover:bg-gray-200 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[70vh]">
                {previewEmail.html_content ? (
                  <iframe
                    srcDoc={previewEmail.html_content}
                    title="Email preview"
                    className="w-full h-[65vh] border-0"
                    sandbox=""
                  />
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>El contenido de este email no fue guardado.</p>
                    <p className="text-sm mt-1">Los emails enviados a partir de ahora incluirán su contenido.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Webhooks Tab */}
        {activeTab === "webhooks" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="mb-3">
                  <CardTitle className="text-lg">Webhooks</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">Conecta InmoPages con tu CRM (GHL, N8N, Zapier, Make)</p>
                </div>
                <Button
                  onClick={() => setShowWebhookModal(true)}
                  className="bg-brand-purple hover:bg-brand-pink whitespace-nowrap"
                  size="sm"
                  data-testid="create-webhook-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Webhook
                </Button>
              </CardHeader>
              <CardContent>
                {webhooks.length === 0 ? (
                  <div className="text-center py-8">
                    <LinkIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay webhooks configurados</p>
                    <p className="text-sm text-gray-400 mt-1">Crea un webhook para enviar datos a tu CRM automaticamente</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {webhooks.map((wh) => (
                      <div key={wh.id} className="border rounded-lg p-4" data-testid={`webhook-${wh.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${wh.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                            <h3 className="font-semibold">{wh.name || "Webhook"}</h3>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleTestWebhook(wh.id)} data-testid={`test-webhook-${wh.id}`}>
                              Test
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleToggleWebhook(wh.id, wh.is_active)}>
                              {wh.is_active ? "Desactivar" : "Activar"}
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteWebhook(wh.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 font-mono break-all">{wh.url}</p>
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {(wh.events || []).map(ev => (
                            <span key={ev} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              {ev === "new_registration" ? "Nuevo Registro" :
                               ev === "new_lead" ? "Nuevo Lead" :
                               ev === "subscription_activated" ? "Suscripcion Activada" :
                               ev === "subscription_expired" ? "Suscripcion Expirada" :
                               ev === "subscription_canceled" ? "Suscripcion Cancelada" : ev}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Webhook Delivery Logs */}
            {webhookLogs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Historial de Entregas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="webhook-logs-table">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 font-medium text-gray-500">Fecha</th>
                          <th className="pb-3 font-medium text-gray-500">Evento</th>
                          <th className="pb-3 font-medium text-gray-500">URL</th>
                          <th className="pb-3 font-medium text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {webhookLogs.map((log, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-3 text-sm text-gray-500">{formatDate(log.created_at)}</td>
                            <td className="py-3 text-sm">{log.event}</td>
                            <td className="py-3 text-sm text-gray-500 font-mono truncate max-w-[200px]">{log.url}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                log.status_code >= 200 && log.status_code < 300 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                              }`}>
                                {log.status_code || "Error"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Learning Videos Tab */}
        {activeTab === "learning" && (
          <Card>
            <CardHeader>
              <div className="mb-3">
                <CardTitle className="text-lg">Videos de Aprendizaje</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Sube videos y controla que planes pueden verlos</p>
              </div>
              <Button
                onClick={() => { setEditingVideo(null); setVideoForm({ title: "", description: "", video_url: "", category: "general", visible_plans: ["basico", "pro", "premium", "agencia"] }); setShowVideoModal(true); }}
                className="bg-brand-purple hover:bg-brand-pink whitespace-nowrap"
                size="sm"
                data-testid="add-video-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Video
              </Button>
            </CardHeader>
            <CardContent>
              {learningVideos.length === 0 ? (
                <div className="text-center py-8">
                  <PlayCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay videos de aprendizaje</p>
                  <p className="text-sm text-gray-400 mt-1">Agrega videos tutoriales para tus usuarios</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {learningVideos.map((video) => (
                    <div key={video.id} className="border rounded-lg p-4" data-testid={`video-${video.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <div className={`w-3 h-3 rounded-full ${video.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                            <h3 className="font-semibold">{video.title}</h3>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{video.category}</span>
                          </div>
                          {video.description && <p className="text-sm text-gray-500 ml-6 mb-2">{video.description}</p>}
                          <p className="text-xs text-gray-400 ml-6 font-mono truncate">{video.video_url}</p>
                          <div className="flex gap-1 mt-2 ml-6 flex-wrap">
                            {(video.visible_plans || []).map(plan => (
                              <span key={plan} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs capitalize">{plan}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button variant="outline" size="sm" onClick={() => handleEditVideo(video)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleToggleVideoActive(video)}>
                            {video.is_active ? "Desactivar" : "Activar"}
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteVideo(video.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{editingVideo ? "Editar Video" : "Nuevo Video"}</h2>
                <button onClick={() => { setShowVideoModal(false); setEditingVideo(null); }}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Titulo</Label>
                  <Input
                    placeholder="Como crear tu primera landing page"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                    data-testid="video-title-input"
                  />
                </div>
                <div>
                  <Label>Descripcion (opcional)</Label>
                  <Input
                    placeholder="Aprende paso a paso..."
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                  />
                </div>
                <div>
                  <Label>URL del Video (YouTube / Vimeo / Loom)</Label>
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={videoForm.video_url}
                    onChange={(e) => setVideoForm({...videoForm, video_url: e.target.value})}
                    data-testid="video-url-input"
                  />
                </div>
                <div>
                  <Label>Categoria</Label>
                  <select
                    value={videoForm.category}
                    onChange={(e) => setVideoForm({...videoForm, category: e.target.value})}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="general">General</option>
                    <option value="primeros-pasos">Primeros Pasos</option>
                    <option value="landing-pages">Landing Pages</option>
                    <option value="leads">Leads y Contactos</option>
                    <option value="marketing">Tips de Marketing</option>
                    <option value="avanzado">Avanzado</option>
                  </select>
                </div>
                <div>
                  <Label className="mb-2 block">Visible para planes</Label>
                  <div className="flex gap-3 flex-wrap">
                    {["basico", "pro", "premium", "agencia"].map(plan => (
                      <label key={plan} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={videoForm.visible_plans.includes(plan)}
                          onChange={() => toggleVideoPlan(plan)}
                          className="rounded"
                        />
                        <span className="text-sm capitalize">{plan}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleSaveVideo}
                  className="w-full bg-brand-purple hover:bg-brand-pink"
                  data-testid="save-video-btn"
                >
                  {editingVideo ? "Guardar Cambios" : "Crear Video"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Webhook Modal */}
        {showWebhookModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Nuevo Webhook</h2>
                <button onClick={() => setShowWebhookModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    placeholder="Mi CRM Webhook"
                    value={webhookForm.name}
                    onChange={(e) => setWebhookForm({...webhookForm, name: e.target.value})}
                    data-testid="webhook-name-input"
                  />
                </div>
                <div>
                  <Label>URL del Webhook</Label>
                  <Input
                    placeholder="https://hooks.zapier.com/..."
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm({...webhookForm, url: e.target.value})}
                    data-testid="webhook-url-input"
                  />
                </div>
                <div>
                  <Label>Secret (opcional)</Label>
                  <Input
                    placeholder="Secret para verificar autenticidad"
                    value={webhookForm.secret}
                    onChange={(e) => setWebhookForm({...webhookForm, secret: e.target.value})}
                    data-testid="webhook-secret-input"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Eventos</Label>
                  <div className="space-y-2">
                    {[
                      { key: "new_registration", label: "Nuevo registro de usuario" },
                      { key: "new_lead", label: "Nuevo lead recibido" },
                      { key: "subscription_activated", label: "Suscripcion activada" },
                      { key: "subscription_expired", label: "Suscripcion expirada" },
                      { key: "subscription_canceled", label: "Suscripcion cancelada" }
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={webhookForm.events.includes(key)}
                          onChange={() => toggleWebhookEvent(key)}
                          className="rounded"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleCreateWebhook}
                  className="w-full bg-brand-purple hover:bg-brand-pink"
                  data-testid="save-webhook-btn"
                >
                  Crear Webhook
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">{selectedUser.name}</h2>
                  <p className="text-gray-500">{selectedUser.email}</p>
                  {selectedUser.phone && (
                    <a href={`https://wa.me/${normalizeWaNumber(selectedUser.phone)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline flex items-center gap-1 mt-1" data-testid="user-detail-whatsapp">
                      WhatsApp: {selectedUser.phone}
                    </a>
                  )}
                  {selectedUser.company_name && (
                    <p className="text-sm text-gray-400 mt-0.5">{selectedUser.company_name}</p>
                  )}
                  {(selectedUser.utm_source || selectedUser.utm_campaign) && (
                    <p className="text-xs text-blue-500 mt-1">
                      Origen: {[selectedUser.utm_source, selectedUser.utm_medium, selectedUser.utm_campaign].filter(Boolean).join(' / ')}
                    </p>
                  )}
                  {selectedUser.google_id && !selectedUser.utm_source && (
                    <p className="text-xs text-red-500 mt-1">Registro con Google</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedUser.role !== "admin" && (
                    <Button 
                      variant="outline" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setShowDeleteConfirm(selectedUser)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar Usuario
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setSelectedUser(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6">
                {/* User Info */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="font-semibold capitalize">{selectedUser.plan}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Estado</p>
                    <p className="font-semibold">{getStatusBadge(selectedUser.subscription_status)}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Propiedades</p>
                    <p className="font-semibold">{selectedUser.property_count}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Leads</p>
                    <p className="font-semibold">{selectedUser.lead_count}</p>
                  </div>
                </div>

                {/* Update User Status */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h4 className="font-medium mb-3">Actualizar Usuario</h4>
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <Label className="text-sm">Plan</Label>
                      <select
                        className="block mt-1 border rounded px-3 py-2"
                        value={selectedUser.plan}
                        onChange={(e) => {
                          handleUpdateUser(selectedUser.id, { plan: e.target.value });
                          setSelectedUser({...selectedUser, plan: e.target.value});
                        }}
                      >
                        <option value="basico">Básico</option>
                        <option value="pro">Pro</option>
                        <option value="premium">Premium</option>
                        <option value="agencia">Agencia</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm">Estado de Suscripción</Label>
                      <select
                        className="block mt-1 border rounded px-3 py-2"
                        value={selectedUser.subscription_status}
                        onChange={(e) => {
                          handleUpdateUser(selectedUser.id, { subscription_status: e.target.value });
                          setSelectedUser({...selectedUser, subscription_status: e.target.value});
                        }}
                      >
                        <option value="trialing">Prueba</option>
                        <option value="active">Activo</option>
                        <option value="expired">Expirado</option>
                        <option value="canceled">Cancelado</option>
                        <option value="past_due">Pago Atrasado</option>
                        <option value="pending_payment">Pago Pendiente</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm">Acceso a Plataforma</Label>
                      <select
                        className="block mt-1 border rounded px-3 py-2"
                        value={selectedUser.is_active ? "true" : "false"}
                        onChange={(e) => {
                          const isActive = e.target.value === "true";
                          handleUpdateUser(selectedUser.id, { is_active: isActive });
                          setSelectedUser({...selectedUser, is_active: isActive});
                        }}
                      >
                        <option value="true">Permitido</option>
                        <option value="false">Bloqueado</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">
                        Bloquear impide el login
                      </p>
                    </div>
                    <div className="w-full md:w-auto">
                      <Label className="text-sm">WhatsApp (10 dígitos MX o E.164)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="text"
                          defaultValue={selectedUser.phone || ""}
                          placeholder="3331234567 o 523331234567"
                          className="border rounded px-3 py-2 w-56"
                          data-testid="edit-user-phone-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const v = e.target.value.trim();
                              handleUpdateUser(selectedUser.id, { phone: v });
                              setSelectedUser({...selectedUser, phone: v});
                              toast.success("WhatsApp actualizado (se normalizará automáticamente)");
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="edit-user-phone-save-btn"
                          onClick={(e) => {
                            const input = e.currentTarget.parentElement.querySelector('input');
                            const v = input.value.trim();
                            handleUpdateUser(selectedUser.id, { phone: v });
                            setSelectedUser({...selectedUser, phone: v});
                            toast.success("WhatsApp actualizado");
                          }}
                        >
                          Guardar
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Se normalizará al guardar (52 al frente para MX)
                      </p>
                    </div>
                    <div className="w-full md:w-auto">
                      <Label className="text-sm">Email Stripe (alias, opcional)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="email"
                          defaultValue={selectedUser.stripe_email || ""}
                          placeholder="email en Stripe distinto al de la plataforma"
                          className="border rounded px-3 py-2 w-72"
                          data-testid="edit-user-stripe-email-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const v = e.target.value.trim();
                              handleUpdateUser(selectedUser.id, { stripe_email: v });
                              setSelectedUser({...selectedUser, stripe_email: v});
                              toast.success("Email Stripe vinculado");
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="edit-user-stripe-email-save-btn"
                          onClick={(e) => {
                            const input = e.currentTarget.parentElement.querySelector('input');
                            const v = input.value.trim();
                            handleUpdateUser(selectedUser.id, { stripe_email: v });
                            setSelectedUser({...selectedUser, stripe_email: v});
                            toast.success("Email Stripe vinculado");
                          }}
                        >
                          Guardar
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Vincula este usuario con un cliente de Stripe que use otro email (ej. tarjeta a nombre de otra persona)
                      </p>
                    </div>
                  </div>
                  
                  {/* Subscription Active Info */}
                  <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Suscripción activa:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        ['active', 'trialing'].includes(selectedUser.subscription_status) 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {['active', 'trialing'].includes(selectedUser.subscription_status) ? 'Sí' : 'No'}
                      </span>
                    </div>
                    {selectedUser.subscription_status === 'expired' && (
                      <p className="text-xs text-red-600 mt-2">
                        ⚠️ Trial/Suscripción expirada. Propiedades pausadas.
                      </p>
                    )}
                    
                    {/* Stripe Subscription Info */}
                    {selectedUser.stripe_subscription_info && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-2">Datos de Stripe:</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-400">Próximo cobro:</span>
                            <span className="ml-1 font-medium text-green-600">
                              {selectedUser.stripe_subscription_info.current_period_end 
                                ? formatDate(selectedUser.stripe_subscription_info.current_period_end)
                                : '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400">Monto:</span>
                            <span className="ml-1 font-medium">
                              ${selectedUser.stripe_subscription_info.plan_amount 
                                ? (selectedUser.stripe_subscription_info.plan_amount / 100).toFixed(2)
                                : '-'} MXN
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Sync with Stripe Button */}
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSyncStripe(selectedUser.id)}
                        className="w-full text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {selectedUser.stripe_subscription_info ? 'Actualizar desde Stripe' : 'Sincronizar con Stripe'}
                      </Button>
                      
                      {/* Generate Payment Link - for users with payment issues */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGeneratePaymentLink(selectedUser.id)}
                        className="w-full text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <LinkIcon className="w-4 h-4 mr-2" />
                        Generar Link de Pago
                      </Button>
                      
                      {/* Reset Password */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const pwd = prompt("Nueva contraseña para " + selectedUser.email + " (mín. 6 caracteres):");
                          if (pwd && pwd.length >= 6) {
                            const token = localStorage.getItem("token");
                            axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, 
                              { new_password: pwd },
                              { headers: { Authorization: `Bearer ${token}` } }
                            ).then(() => toast.success("Contraseña actualizada"))
                             .catch((e) => toast.error(e.response?.data?.detail || "Error"));
                          } else if (pwd) {
                            toast.error("La contraseña debe tener al menos 6 caracteres");
                          }
                        }}
                        className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid="admin-reset-password-btn"
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        Restablecer Contraseña
                      </Button>
                      
                      {/* Email verification helpers - only show if email is NOT verified */}
                      {!selectedUser.email_verified && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              axios.post(`${API}/admin/users/${selectedUser.id}/resend-verification`)
                                .then(() => toast.success("Código de verificación reenviado al correo del usuario"))
                                .catch((e) => toast.error(e.response?.data?.detail || "Error"));
                            }}
                            className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                            data-testid="admin-resend-verification-btn"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Reenviar código de verificación
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (!window.confirm(`¿Verificar manualmente el email de ${selectedUser.email}? Úsalo solo cuando el correo de verificación no llegue (cae en spam, error de servidor, etc.)`)) return;
                              axios.post(`${API}/admin/users/${selectedUser.id}/verify-email`)
                                .then(() => {
                                  toast.success("Email verificado manualmente");
                                  loadData();
                                  setSelectedUser({ ...selectedUser, email_verified: true });
                                })
                                .catch((e) => toast.error(e.response?.data?.detail || "Error"));
                            }}
                            className="w-full text-green-700 border-green-300 hover:bg-green-50"
                            data-testid="admin-verify-email-btn"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Verificar email manualmente
                          </Button>
                        </>
                      )}
                      
                      {/* Send Reminder Email - for expired users */}
                      {selectedUser.subscription_status === 'expired' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendExpiredReminders(selectedUser.id)}
                          className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Enviar Recordatorio
                        </Button>
                      )}
                      
                      <p className="text-xs text-gray-400 text-center">
                        {selectedUser.subscription_status === 'expired' 
                          ? 'Usuario con suscripción expirada'
                          : !selectedUser.stripe_customer_id 
                            ? 'Sincroniza primero para ver datos de Stripe'
                            : 'Link de pago útil si falla el cobro automático'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Properties */}
                <h3 className="font-semibold mb-4">Propiedades del Usuario</h3>
                {userProperties.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Sin propiedades</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {userProperties.map((prop) => (
                      <div key={prop.id} className="border rounded-lg p-4">
                        <h4 className="font-medium">{prop.title}</h4>
                        <p className="text-brand-purple font-bold">{formatPrice(prop.price)}</p>
                        <p className="text-sm text-gray-500">{prop.location}</p>
                        <div className="mt-3 flex gap-2">
                          <Link to={`/propiedades/${prop.slug}`} target="_blank">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-1" /> Ver
                            </Button>
                          </Link>
                          <Link to={`/admin/properties/edit/${prop.id}`} state={{ from: 'super-admin' }}>
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4 mr-1" /> Editar
                            </Button>
                          </Link>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteProperty(prop.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Crear Nuevo Usuario</h2>
                <Button variant="ghost" onClick={() => setShowCreateUserModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                <div>
                  <Label htmlFor="new-name">Nombre *</Label>
                  <Input
                    id="new-name"
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                    placeholder="Juan Pérez"
                    required
                    data-testid="new-user-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-email">Email *</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                    placeholder="juan@ejemplo.com"
                    required
                    data-testid="new-user-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-password">Contraseña *</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                    placeholder="Contraseña segura"
                    required
                    data-testid="new-user-password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-company">Empresa (opcional)</Label>
                  <Input
                    id="new-company"
                    value={newUserForm.company_name}
                    onChange={(e) => setNewUserForm({...newUserForm, company_name: e.target.value})}
                    placeholder="Inmobiliaria XYZ"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-phone">WhatsApp *</Label>
                  <Input
                    id="new-phone"
                    value={newUserForm.phone}
                    onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})}
                    placeholder="+52 333 123 4567"
                    required
                    data-testid="new-user-phone"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowCreateUserModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-brand-purple hover:bg-brand-pink"
                    disabled={creatingUser}
                    data-testid="submit-create-user"
                  >
                    {creatingUser ? "Creando..." : "Crear Usuario"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">¿Eliminar Usuario?</h3>
                <p className="text-gray-600 mb-2">
                  Estás a punto de eliminar a <strong>{showDeleteConfirm.name}</strong>
                </p>
                <p className="text-sm text-red-600 mb-6">
                  Esto eliminará todas sus propiedades, leads y datos asociados. Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowDeleteConfirm(null)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={() => handleDeleteUser(showDeleteConfirm.id)}
                    data-testid="confirm-delete-user"
                  >
                    Sí, Eliminar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdmin;
